import { SpyneTrait, SpyneAppProperties, ViewStream } from 'spyne';
import { DeckBlockView } from 'components/deck/deck-block-view.js';
import DeckStackPairTmpl from 'components/deck/templates/deck-stack-pair.tmpl.html';

// The schema's 14 `template` names reduce to three layouts — title, single and
// split — and which one a slide gets is read from its content, not its name.
// Every layout wraps its regions twice. The stage is the slideshow's 16:9
// frame, scaled uniformly to fit the screen; the content inside is laid out at
// canvas size and, only if it overruns the frame, scaled down to fit it.
const layoutTemplates = {
  split:
    '<div class="deck-slide-stage"><div class="deck-slide-content"><div class="deck-region deck-region--main"></div><div class="deck-region deck-region--aside"></div></div></div>',
};
const singleRegionTmpl =
  '<div class="deck-slide-stage"><div class="deck-slide-content"><div class="deck-region deck-region--main"></div></div></div>';

export class DeckSlideTraits extends SpyneTrait {
  constructor(context) {
    let traitPrefix = 'deckSlide$';
    super(context, traitPrefix);
  }

  static deckSlide$GetLayoutTemplate(layout) {
    return layoutTemplates[layout] || singleRegionTmpl;
  }

  // Adds the derived `layout` key only — authored keys are never overwritten.
  static deckSlide$PrepareData(data = {}) {
    const blocks = DeckSlideTraits.deckSlide$VisibleBlocks(data.content);
    const hasAside = blocks.some(DeckSlideTraits.deckSlide$IsAsideBlock);

    if (data.template === 'title') {
      data.layout = 'title';
    } else {
      data.layout = hasAside === true ? 'split' : 'single';
    }

    return data;
  }

  // isStagedContent blocks are built but held dark until the ship flag flips.
  static deckSlide$VisibleBlocks(content = []) {
    const showStaged =
      SpyneAppProperties.getProp('deckShowStagedContent') === true;

    return content.filter(
      (block) => showStaged === true || block?.isStagedContent !== true,
    );
  }

  // Exhibits take the aside column. A staged exhibit stays in the main column,
  // so flipping the flag never changes a slide's layout. The funds lockup is a
  // second row of the statRow's column, not an exhibit beside it.
  static deckSlide$IsAsideBlock(block = {}) {
    return (
      block?.type === 'exhibit' &&
      block.isStagedContent !== true &&
      block.className !== 'exhibit-funds'
    );
  }

  // A stack-left exhibit directly followed by stack-right renders as one pair,
  // with the migration arrow between them.
  static deckSlide$GroupStackPairs(blocks = []) {
    const entries = [];

    blocks.forEach((block, i) => {
      const previous = blocks[i - 1];
      const closesPair =
        block?.className === 'stack-right' &&
        previous?.className === 'stack-left';

      if (closesPair === true) {
        entries[entries.length - 1] = { isPair: true, stacks: [previous, block] };
        return;
      }

      entries.push({ block });
    });

    return entries;
  }

  static deckSlide$AppendStackPair(stacks = [], selector, slideLayout) {
    const pair = new ViewStream({
      class: 'deck-stack-pair',
      template: DeckStackPairTmpl,
    });
    this.appendView(pair, selector);

    const [left, right] = stacks;
    const slotSelector = (side) => `${pair.props.id$} .deck-stack-slot--${side}`;

    this.appendView(
      new DeckBlockView({ data: left, slideLayout }),
      slotSelector('left'),
    );
    this.appendView(
      new DeckBlockView({ data: right, slideLayout }),
      slotSelector('right'),
    );
  }

  // Slideshow: the addressed slide shows and fits itself to the screen; every
  // other slide steps aside (the container's data-slideshow hides them).
  static deckSlide$OnDisplaySlide(e, props = this.props) {
    const isCurrent = Number(e.payload?.slideNum) === Number(props.slideNum);

    props.el$('.deck-slide-stage').el.style.transform = '';
    props.el$('.deck-slide-content').el.style.transform = '';
    props.el.classList.toggle('is-current', isCurrent);

    if (isCurrent === true) {
      // Measured on the next frame: the fullscreen layout attribute lands in
      // this same tick, after this handler, and the stage must be measured
      // at its full-bleed size.
      requestAnimationFrame(() => this.deckSlide$FitStageToScreen());
    }
  }

  // Two uniform scales, so the aspect ratio never changes: the frame is
  // scaled to contain in the screen (up or down); the content is scaled down
  // only when it is taller or wider than the frame's padding box.
  static deckSlide$FitStageToScreen(props = this.props) {
    const stage = props.el$('.deck-slide-stage').el;
    const content = props.el$('.deck-slide-content').el;
    const inner = (el) => {
      const cs = getComputedStyle(el);
      return {
        width: el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight),
        height: el.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom),
      };
    };

    const frameInner = inner(stage);
    const contentScale = Math.min(
      1,
      frameInner.width / content.offsetWidth,
      frameInner.height / content.offsetHeight,
    );
    content.style.transform =
      contentScale < 1 ? `scale(${contentScale.toFixed(4)})` : '';

    const screen = inner(props.el);
    const frameScale = Math.min(
      screen.width / stage.offsetWidth,
      screen.height / stage.offsetHeight,
    );
    stage.style.transform = `scale(${frameScale.toFixed(4)})`;

    // Read by the fit report: 1 means the slide fits its canvas as designed.
    props.el.dataset.contentScale = contentScale.toFixed(2);
  }

  static deckSlide$OnSlideshowDeactivated(e, props = this.props) {
    props.el.classList.remove('is-current');
    props.el$('.deck-slide-stage').el.style.transform = '';
    props.el$('.deck-slide-content').el.style.transform = '';
    delete props.el.dataset.contentScale;
  }

  static deckSlide$onRendered(props = this.props) {
    const { layout, content } = props.data;
    const blocks = DeckSlideTraits.deckSlide$VisibleBlocks(content);
    const mainSelector = '.deck-region--main';
    const asideSelector =
      layout === 'split' ? '.deck-region--aside' : mainSelector;

    DeckSlideTraits.deckSlide$GroupStackPairs(blocks).forEach((entry) => {
      if (entry.isPair === true) {
        this.deckSlide$AppendStackPair(entry.stacks, asideSelector, layout);
        return;
      }

      const selector = DeckSlideTraits.deckSlide$IsAsideBlock(entry.block)
        ? asideSelector
        : mainSelector;

      this.appendView(
        new DeckBlockView({ data: entry.block, slideLayout: layout }),
        selector,
      );
    });
  }
}
